import axios from 'axios'
import {getAdminToken} from '../../auth/_requests'
import {
  AccountIssue,
  IssuesResponse,
  IssueStatus,
  AccountSearchResult,
  IssueAttachment,
  WaitingState,
  IssueUrgency,
  AdminOption,
} from './_models'

const API_URL = process.env.REACT_APP_API_URL

// Axios instance that auto-injects the admin bearer token (mirrors the accounts
// module). Used for all /admin/issues calls.
const adminApi = axios.create({withCredentials: true})
adminApi.interceptors.request.use((config) => {
  const token = getAdminToken()
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

export function getIssues(
  params: {
    status?: string
    q?: string
    assigned_to?: string
    department?: string
    waiting?: string
    from?: string // calendar range, YYYY-MM-DD
    to?: string
    unscheduled?: 1
  } = {}
): Promise<{data: IssuesResponse}> {
  return adminApi.get(`${API_URL}/admin/issues`, {params})
}

export function getIssueAdmins(): Promise<{data: {admins: AdminOption[]}}> {
  return adminApi.get(`${API_URL}/admin/issues/admins`)
}

export function createIssue(data: {
  title: string
  description?: string
  status?: IssueStatus
  account_ids?: string[]
  account_id?: string
  attachments?: IssueAttachment[]
  created_by?: 'admin' | 'client'
  department?: string
  assigned_to?: string
  due_date?: string
}): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.post(`${API_URL}/admin/issues`, data)
}

// Upload one media file, returns its public URL + metadata.
export function uploadIssueMedia(file: File): Promise<{data: IssueAttachment}> {
  const fd = new FormData()
  fd.append('file', file)
  return adminApi.post(`${API_URL}/admin/issues/upload`, fd, {
    headers: {'Content-Type': 'multipart/form-data'},
  })
}

// Log in as (impersonate) an account; returns a client token for auto-login.
export function switchToAccount(accountId: string): Promise<{data: {success: boolean; token?: string}}> {
  return adminApi.get(`${API_URL}/admin/switch-account/${accountId}`)
}

export function updateIssue(
  id: string,
  data: {
    title?: string
    description?: string
    status?: IssueStatus
    urgency?: IssueUrgency
    waiting?: WaitingState
    assigned_to?: string | null
    department?: string | null
    due_date?: string | null
  }
): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.patch(`${API_URL}/admin/issues/${id}`, data)
}

export function deleteIssue(id: string): Promise<{data: {ok: boolean}}> {
  return adminApi.delete(`${API_URL}/admin/issues/${id}`)
}

export function addIssueAccounts(
  id: string,
  account_ids: string[]
): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.post(`${API_URL}/admin/issues/${id}/accounts`, {account_ids})
}

export function updateIssueAccount(
  id: string,
  accountId: string,
  data: {status?: IssueStatus; note?: string}
): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.patch(`${API_URL}/admin/issues/${id}/accounts/${accountId}`, data)
}

export function removeIssueAccount(
  id: string,
  accountId: string
): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.delete(`${API_URL}/admin/issues/${id}/accounts/${accountId}`)
}

export function replyIssue(
  id: string,
  text: string,
  attachments: IssueAttachment[] = []
): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.post(`${API_URL}/admin/issues/${id}/reply`, {text, attachments})
}

export function confirmIssueFixed(id: string): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.patch(`${API_URL}/admin/issues/${id}/confirm-fixed`, {})
}

export function unconfirmIssueFixed(id: string): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.patch(`${API_URL}/admin/issues/${id}/unconfirm-fixed`, {})
}

export function generateIssueFixPrompt(
  id: string
): Promise<{data: {relevant: boolean; prompt: string; reason: string}}> {
  return adminApi.post(`${API_URL}/admin/issues/${id}/ai-fix-prompt`, {})
}

export function confirmIssueAccountFixed(id: string, accountId: string): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.patch(`${API_URL}/admin/issues/${id}/accounts/${accountId}/confirm`, {})
}

export function deleteIssueReply(id: string, replyId: string): Promise<{data: {issue: AccountIssue}}> {
  return adminApi.delete(`${API_URL}/admin/issues/${id}/replies/${replyId}`)
}

// Account picker for the checklist — reuses the existing admin search endpoint.
export function searchAccounts(q: string): Promise<{data: {accounts: AccountSearchResult[]}}> {
  return adminApi.get(`${API_URL}/admin/search-accounts`, {
    params: {q, light: 1, search_all: 1, limit: 20},
  })
}
